<template>
  <div class="modal-card">
    <b-loading :isFullPage="false" :active="isLoading" />

    <header class="modal-card-head">
      <span class="modal-card-title is-size-5 has-text-centered">
        {{
          $attrs.processCover
            ? General.MESSAGES.GIVE_KNOWLEDGE_COVER_DESCRIPTION
            : General.GIVE_KNOWLEDGE
        }}
      </span>
      <div class="close-button cursor-pointer" @click.stop="$emit('close')">
        <b-icon icon="close" />
      </div>
    </header>

    <div class="modal-content-container">
      <section class="spacing-mb-10">
        <div class="custom-right">
          <b-button
            :type="isContrast ? 'is-white-dark' : 'is-green-lawn'"
            @click="openModalGiveKnowledge"
          >
            {{ General.EXTERNAL_GIVE_SCIENCE }}
          </b-button>
        </div>
        <b-field customClass="has-text-color-7" :label="General.ORGAN_ENTITY">
          <b-autocomplete
            v-model="entityName"
            :placeholder="General.PLACEHOLDER.SEARCH_ORGAN_ENTITY"
            keepFirst
            :openOnFocus="openOnFocus"
            :data="filteredParts(entityName)"
            field="name"
            @select="option => (selectedEntity = option)"
          />
        </b-field>
        <b-field customClass="has-text-color-7" :label="General.USER">
          <b-autocomplete
            class="overflow-list"
            :disabled="!entityName"
            :placeholder="General.PLACEHOLDER.TYPE_SEARCH_FULL_USER_UNIT"
            :loading="isFetching"
            :openOnFocus="openOnFocus"
            :data="getUsersFromSearchFormat"
            field="displayName"
            :clearable="clearable"
            :maxHeight="100"
            expanded
            keepFirst
            clearOnSelect
            @typing="filteredUsersData"
            @select="selectUser"
          />
        </b-field>
        <draggable v-if="giveKnowledge.length > 0" v-model="giveKnowledge">
          <CustomSubscriberList
            v-for="(item, index) in giveKnowledge"
            :key="index"
            :subscriber="item"
            :editFunction="openModalGiveKnowledge"
          />
        </draggable>
        <b-field>
          <template slot="label">
            <div
              class="has-text-color-7 is-6 has-text-weight-semibold is-flex is-align-items-center"
            >
              {{ General.NOTES }}
              <span class="optional-label ml-5">{{ General.OPTIONAL }}</span>
            </div>
          </template>
          <b-input
            v-model="observation"
            maxlength="200"
            type="textarea"
            :placeholder="General.PLACEHOLDER.EMPTY_OBSERVATION"
          ></b-input>
        </b-field>
        <b-checkbox
          v-if="giveKnowledge.length"
          v-model="checkLGPD"
          :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
          class="has-text-color-7 has-text-weight-medium"
          >{{ App.ModalSelectUser.LGPD }}</b-checkbox
        >
        <div class="spacing-py-20"></div>
      </section>
    </div>
    <footer class="modal-card-foot is-justify-content-flex-end">
      <div class="buttons">
        <b-button
          :type="isContrast ? 'is-white-dark' : 'is-green-lawn'"
          :disabled="!giveKnowledge.length"
          @click="sendGiveScienceUsers"
        >
          {{ General.GIVE_KNOWLEDGE.toUpperCase() }}
        </b-button>
        <b-button
          :type="isContrast ? 'is-white-dark' : 'is-orange'"
          outlined
          @click="$emit('close')"
        >
          {{ General.CANCEL.toUpperCase() }}
        </b-button>
      </div>
    </footer>
  </div>
</template>

<script>
import draggable from "vuedraggable";
import debounce from "lodash/debounce";
import { mapGetters, mapActions, mapMutations } from "vuex";
import { NUP, applyMask } from "@/utils/protocols";
import { App, General } from "@/utils/constants";
import ModalExternalAssign from "@/views/App/ModalExternalAssign.vue";
import CustomSubscriberList from "@/components/CustomSubscriberList";
import ListGiveKnowledge from "@/components/ListGiveKnowledge";
import { v4 as uuidv4 } from "uuid";

export default {
  name: "ModalSelectUser",

  components: {
    CustomSubscriberList,
    draggable
  },

  props: {
    updateFiles: {
      type: Function,
      default: null
    },
    fileIds: {
      type: Array,
      default: () => []
    },
    process: {
      type: Object,
      default: () => null
    },
    processGiveKnowledge: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      App,
      General,
      isLoading: false,
      selectedUser: null,
      isFetching: false,
      openOnFocus: false,
      entityName: "",
      selectedEntity: null,
      responsible: null,
      clearable: false,
      observation: "",
      checkLGPD: false
    };
  },

  computed: {
    ...mapGetters({
      user: "getCurrentUser",
      getUsersFromSearch: "getUsersFromSearch",
      getSubscribers: "getSubscribers",
      filteredParts: "getFilteredParts",
      isContrast: "isContrast"
    }),

    giveKnowledge: {
      get() {
        return this.getSubscribers;
      },
      set(value) {
        const newList = value.map((user, index) => ({
          ...user,
          order: index
        }));
        this.$store.commit("updateSubscriderOrder", newList);
      }
    },

    getUsersFromSearchFormat() {
      return this.getUsersFromSearch.map(user => ({
        ...user,
        displayName: `${user.name}${
          user.capacity_selected
            ? " - " + user.capacity_selected.abbreviation
            : ""
        }`
      }));
    }
  },

  watch: {
    giveKnowledge: {
      handler(value) {
        if (value.length == 0) this.checkLGPD = false;
      }
    }
  },

  beforeMount() {
    this.clearError();
    this.$store.commit("resetSubscriberStore");
    this.entityName = this.user?.capacity.part_description;
  },

  methods: {
    ...mapActions({
      userFilter: "userGenericFilterRequest",
      giveKnowledgeTrigger: "giveKnowledgeTrigger"
    }),

    ...mapMutations({
      clearError: "clearError",
      setListGiveKnowledge: "setListGiveKnowledge",
      clearListGiveKnowledge: "clearListGiveKnowledge"
    }),

    close() {
      this.$parent.close();
      this.$store.commit("resetSubscriberStore");
      this.updateFiles && this.updateFiles();
    },

    selectUser(selectedUser) {
      this.clearError();
      if (selectedUser) {
        this.$store.commit("addSubscriber", selectedUser);
      }
    },

    validate() {
      const msg = [];
      if (!this.checkLGPD) {
        msg.push(App.ModalSelectUser.VALIDATE_LGPD);
      }

      if (msg.length > 0) {
        this.snackToast.open({
          message: `${msg.join(", ")}.`,
          type: "is-danger",
          duration: 8000
        });
        return false;
      }
      return true;
    },

    async sendGiveScienceUsers() {
      if (this.validate()) {
        this.isLoading = true;
        const usersFormat = this.prepareUsers();
        const usersExternal = this.prepareExternalUsers();
        this.setListGiveKnowledge([...usersFormat, ...usersExternal]);
        this.snackQueue.open({
          id: "give_knowledge",
          parent: this,
          closeAction: this.clearListGiveKnowledge,
          component: ListGiveKnowledge
        });
        this.giveKnowledgeTrigger();
        this.close();
        this.isLoading = false;
      }
    },

    prepareUsers() {
      const usersInternal = this.giveKnowledge.filter(sub => !sub.isExternal);
      const group = uuidv4();
      if (this.fileIds?.length) {
        return this.fileIds.reduce((acc, current) => {
          acc.push(
            ...usersInternal.map(sub => ({
              process_file_id: current.id,
              process_file_name: current.attachment_name,
              user: sub,
              LGPD: this.checkLGPD,
              status: "pending",
              percentage: 0,
              index: uuidv4(),
              group_by: `${group}_${sub.id}`,
              processId: this.process.id,
              isExternal: false,
              isProcess: Boolean(this.processGiveKnowledge),
              observation: this.observation
            }))
          );
          return acc;
        }, []);
      }
      return usersInternal.map(sub => ({
        process_id: this.process.id,
        process_file_name: applyMask(NUP, this.process.nup),
        user: sub,
        LGPD: this.checkLGPD,
        index: uuidv4(),
        group_by: `${group}_${sub.id}`,
        processId: this.process.id,
        status: "pending",
        percentage: 0,
        isExternal: false,
        isProcess: Boolean(this.processGiveKnowledge),
        observation: this.observation
      }));
    },

    prepareExternalUsers() {
      const usersExternal = this.giveKnowledge.filter(sub => sub.isExternal);
      const group = uuidv4();
      if (this.fileIds?.length) {
        return this.fileIds.reduce((acc, current) => {
          acc.push(
            ...usersExternal.map(sub => ({
              process_file_id: current.id,
              process_file_name: current.attachment_name,
              user: sub,
              LGPD: this.checkLGPD,
              status: "pending",
              percentage: 0,
              index: uuidv4(),
              group_by: `${group}_${sub.email}`,
              processId: this.process.id,
              isExternal: true,
              isProcess: Boolean(this.processGiveKnowledge),
              observation: this.observation
            }))
          );
          return acc;
        }, []);
      }
      return usersExternal.map(sub => ({
        process_file_name: applyMask(NUP, this.process.nup),
        user: sub,
        LGPD: this.checkLGPD,
        index: uuidv4(),
        group_by: `${group}_${sub.email}`,
        status: "pending",
        percentage: 0,
        processId: this.process.id,
        isExternal: true,
        isProcess: Boolean(this.processGiveKnowledge),
        observation: this.observation
      }));
    },

    filteredUsersData: debounce(async function(name) {
      if (name === "") {
        this.$store.commit("setUsersFromSearch", []);
        return;
      }
      this.isFetching = true;
      await this.userFilter({
        part_id: this.selectedEntity
          ? this.selectedEntity.id
          : this.user.capacity.part_id,
        name
      });
      this.isFetching = false;
    }, 500),

    addExternalAssign(newUser) {
      this.giveKnowledge.push({ ...newUser, bonds: [] });
    },

    editExternalAssign(editUser) {
      const userExternalIndex = this.giveKnowledge.findIndex(
        user => user.id === editUser.id
      );
      this.giveKnowledge[userExternalIndex] = editUser;
      this.giveKnowledge = [...this.giveKnowledge];
    },

    openModalGiveKnowledge(editItem) {
      this.$buefy.modal.open({
        parent: this,
        props: {
          isGiveKnowledge: true,
          edit: editItem?.id ? editItem : undefined,
          onClose: editItem?.id
            ? this.editExternalAssign
            : this.addExternalAssign
        },
        component: ModalExternalAssign,
        hasModalCard: true,
        trapFocus: true
      });
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables";

.modal-card {
  display: flex;
  justify-content: space-between;
  min-height: 300px;
  max-height: 80vh;
  background-color: $color-1;

  @media only screen and (max-width: $tablet) {
    width: 100%;
  }
}

.modal-card div.card-body {
  display: flex;
  align-content: flex-start;
  flex-direction: column;
  flex: 1;
}

.custom-self-align-end {
  align-self: flex-end;
}

.modal-content-container {
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  justify-content: space-between;
  overflow: auto;
  padding: 0 20px;
}

.custom-right {
  justify-content: flex-end;
  display: flex;
}

.optional-label {
  font-size: 12px;
  font-weight: 300;
  color: $color-13;
}
</style>
